import bindAllOfThis from "src/utils/BindThisHelper";
import { DropZone, DropZoneConfig, DropZoneType, TiledDropZone } from "src/games/shared/dropzone/DropZone";
import DropZoneSprite from 'src/games/shared/dropzone/DropZoneSprite';
import initializeInteractiveCursor from 'src/games/shared/InteractiveCursorUtil';
import { PhaserGeneralGameObjectType, TiledGameObject } from 'src/games/shared/phaserHelpers/HandyTypes';
import { GameState } from '../objects/GameState';
import TranslateApi from 'src/utils/TranslateHelper';
import { PhaserButtonConfig, PhaserButton } from 'src/games/shared/Button';
import { DeskItem } from '../objects/DeskItem';
import { Utilities } from '../objects/Utilities';
import { PhaserModal, ModalOption } from 'src/games/shared/Modal';
import { Color } from "src/games/shared/ColorValue";
import { EndGameScene } from "src/games/OrganizeDeskGame/scenes/EndGameScene";

export class DeskScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;
    objectLayer: Phaser.Tilemaps.ObjectLayer;
    dropZoneLayer: Phaser.Tilemaps.ObjectLayer;
    deskTop: Phaser.GameObjects.Image;
    dropZones: DropZone[];
    allDeskItems: DropZoneSprite[];
    itemID: number;
    sponge: DropZoneSprite;
    spill: Phaser.GameObjects.Image;
    cart: Phaser.Geom.Rectangle;
    garbageItems: DropZoneSprite[];

    private translateString = "OrganizeDesk.Phaser.DeskScene.";

    constructor() {
        super({
            key: "DeskScene" + Math.random()
        });
    }

    preload(): void {
        this.load.atlas("assets", require("/src/assets/qci/atlas/gameDeskCleanup.png"), require("/src/assets/qci/atlas/gameDeskCleanup.json"));
        this.load.image('background', require("/src/assets/qci/backgrounds/gameCleanDesk.jpg"));

        // Load different desk layouts and their corresponding finished layout pictures
        this.load.tilemapTiledJSON('desk_5', require("/src/assets/qci/maps/organizeDeskGame/organize_desk_game_desk_2.json"));
        this.load.tilemapTiledJSON('desk_6', require("/src/assets/qci/maps/organizeDeskGame/organize_desk_game_desk_3.json"));
        this.load.tilemapTiledJSON('desk_3', require("/src/assets/qci/maps/organizeDeskGame/organize_desk_game_desk_4.json"));
        this.load.tilemapTiledJSON('desk_4', require("/src/assets/qci/maps/organizeDeskGame/organize_desk_game_desk_5.json"));
        this.load.tilemapTiledJSON('desk_2', require("/src/assets/qci/maps/organizeDeskGame/organize_desk_game_desk_6.json"));
        this.load.tilemapTiledJSON('desk_1', require("/src/assets/qci/maps/organizeDeskGame/organize_desk_game_desk_1.json"));
    }

    create(): void {
        bindAllOfThis(this, DeskScene.prototype);
        this.initialize();
        this.generateScene();
        this.generateEvents();
        initializeInteractiveCursor(this);
    }

    initialize() {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: GameState.current.mapKey });
        this.objectLayer = this.map.getObjectLayer('Object Layer');
        this.dropZoneLayer = this.map.getObjectLayer('DropZone Layer');
        this.dropZones = [];
        this.allDeskItems = [];
        this.itemID = 0;
        this.garbageItems = [];
    }

    generateEvents() {
        this.input.on('dragstart', (pointer: Phaser.Input.Pointer, gameObject: DropZoneSprite) => {
            if (this.tweens.isTweening(gameObject)) {
                this.tweens.killTweensOf(gameObject);
            }
            gameObject.x = pointer.downX;
            gameObject.y = pointer.downY;
            gameObject.setAngle();
            this.children.bringToTop(gameObject);
        });

        this.input.on('drag', (pointer: Phaser.Input.Pointer, gameObject: PhaserGeneralGameObjectType, dragX: number, dragY: number) => {
            gameObject.x = pointer.x;
            gameObject.y = pointer.y;
        });

        this.input.on('dragend', (pointer: Phaser.Input.Pointer, gameObject: PhaserGeneralGameObjectType, dropped: boolean) => {
            // Move dropped object back to start if not dropped onto a dropzone
            if (!dropped) {
                this.putItemBack(gameObject);
            }
        });
    }

    generateScene() {
        let background = this.add.image(0, 0, "background");
        background.setOrigin(0);
        background.setDisplaySize(this.gameWidth, this.gameHeight);

        var textStyle = { fontSize: 20, color: "#ffffff", fontFamily: "Russo One, Arial" };
        var finishedLabel = this.add.text(100, 150, TranslateApi.translate(this.translateString + "desired"), textStyle);
        finishedLabel.setX(this.gameWidth / 2 - finishedLabel.width / 2 + 20);

        var starPilot = this.add.image(-50, 375, "pilots", "c");
        starPilot.setOrigin(0);
        starPilot.setDisplaySize(300, 500);

        var deskNumber = this.add.text(0, 30, TranslateApi.translate(this.translateString + "desk") + " " + GameState.current.deskNumber, { fontSize: 30, color: "#000000", fontFamily: "Russo One, Arial" });

        this.addObjects();
        this.addDropZones();
        if (GameState.current.spill) {
            this.addSpill();
        }

        this.addFinishedDeskLayout();

        var config = new PhaserButtonConfig();
        config.backgroundColor = Color.KUDER_PRIMARY_BUTTON;
        config.smartHeight = true;
        var text = TranslateApi.translate(this.translateString + "check");
        var button = new PhaserButton(this, this.gameWidth - 200, 80, 150, 50, text, config);
        button.on('buttonClicked', () => {
            if (button.isEnabled()) {
                button.setEnabled(false);
                this.checkDeskButtonHandler(button);
            }
        });

        var hintButtonText = TranslateApi.translate(this.translateString + "hint");
        config.backgroundColor = Color.KUDER_SECONDARY_BUTTON;
        button = new PhaserButton(this, this.gameWidth - 200, 160, 150, 50, hintButtonText, config);
        button.on('buttonClicked', () => {
            this.giveHint();
        });

        deskNumber.setX(this.gameWidth - deskNumber.width - 75);
    }

    putItemBack(gameObject: PhaserGeneralGameObjectType): void {
        var actualItem = DeskItem.ALL_ITEMS.concat(DeskItem.CLEANING_ITEM).find((i: DeskItem) => {
            return i.name === gameObject.name;
        }) as DeskItem;

        // If it was dropped on the pile
        // it's a normal desk item
        // it started on the cart and wasn't accepted
        // Put it back in the pile and reset it
        if (Phaser.Geom.Intersects.RectangleToRectangle(this.cart, gameObject.getBounds()) &&
            this.isNormalDeskItem(gameObject) ||
            this.cart.contains(gameObject.input.dragStartX, gameObject.input.dragStartY)) {
            gameObject.x = actualItem.startX;
            gameObject.y = actualItem.startY;
            gameObject.setAngle(actualItem.angle);
        }
        else {
            // Reset item
            gameObject.x = gameObject.input.dragStartX;
            gameObject.y = gameObject.input.dragStartY;
            if (gameObject.type === "deskItem" || gameObject.name === "sponge") {
                gameObject.setAngle(gameObject.getData('startAngle'));
            }
        }
        this.children.bringToTop(gameObject);
    }

    isNormalDeskItem(gameObject): boolean {
        return (gameObject.name !== "sponge" && gameObject.name !== "paper_wad" && gameObject.name !== "plane");
    }

    addFinishedDeskLayout(): void {
        this.map.getObjectLayer("Finished").objects.forEach((ob: TiledGameObject) => {
            var item = DeskItem.ALL_ITEMS.find((i: DeskItem) => {
                return i.name === ob.name;
            });
            let deskItem = this.add.image(ob.x, ob.y, "assets", item.name);
            deskItem.setOrigin(0);
            deskItem.setDisplaySize(item.display_width * 0.75, item.display_height * 0.75);
        });
    }

    addObjects(): void {
        console.warn("this.objectLayer", this.objectLayer); // JB - Remove this console.warn
        this.objectLayer.objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "deskTop") {
                this.deskTop = this.add.image(ob.x, ob.y, "pixel");
                this.deskTop.setOrigin(0);
                this.deskTop.setDisplaySize(ob.width, ob.height);
                this.deskTop.setVisible(false);
            }
            else if (ob.name === "cart") {
                this.cart = new Phaser.Geom.Rectangle(ob.x, ob.y, ob.width, ob.height);
            }
        });

        this.createDeskItems();
    }

    addDropZones(): void {
        this.dropZoneLayer.objects.forEach((ob: TiledDropZone) => {
            var x = ob.x + ob.width / 2;
            var y = ob.y + ob.height / 2;

            if (ob.type === "deskItem") {
                this.createCartItem(ob);
            }
            else {
                if (ob.type === "cleaningItem") {
                    this.createCleaningItem(ob);
                }

                var config: DropZoneConfig = {
                    height: ob.height,
                    width: ob.width,
                    square: ob.properties.square,
                    backgroundTexture: ob.properties.asset,
                    backgroundFrame: "",
                    ellipse: ob.properties.ellipse,
                    lineColor: null,
                    dragLineColor: 0x00000,
                    allowStacking: ob.properties.allowStacking,
                    allowRedrop: ob.properties.allowRedrop,
                    allowResize: false,
                    allowSnapToCenter: ob.properties.allowSnapToCenter,
                    allowDropzoneDisappear: false,
                    acceptedKeys: (ob.properties.acceptedKeys || "").split(','),
                };
                var dropZone = new DropZone(this, x, y, config);
                dropZone.type = ob.type;
                dropZone.name = ob.name;
                dropZone.angle = ob.rotation;
                dropZone.on('drop', (dropzone: DropZone, gameObject: DropZoneType, accepted: boolean) => {
                    var item;
                    if (accepted) {
                        item = this.allDeskItems.find((i: DropZoneSprite) => {
                            return i.getData('id') === gameObject.getData('id');
                        });
                        item.setData('dropped', true);
                        // Accepted items thrown in trash just disappear
                        if (dropzone.name === "trash") {
                            gameObject.setVisible(false);
                        }
                    }
                    else {
                        this.putItemBack(gameObject);
                    }
                });
                this.dropZones.push(dropZone);
            }
        });
    }

    /**
     * Adds a random "spill" to the desk.
     * Initialized as not "cleaned"
     */
    addSpill(): void {
        var spillColors = [0x68f442, 0x381696, 0xe03d18, 0x9820aa];
        var minX = this.deskTop.getBottomLeft().x - 10;
        var minY = this.deskTop.getTopLeft().y - 10;
        var maxX = this.deskTop.getBottomRight().x - 10;
        var maxY = this.deskTop.getBottomLeft().y - 10;

        var mess = DeskItem.SPILL;
        var point = Utilities.getRandomPoint(minX + mess.display_width / 4, maxX - mess.display_width / 4, minY + mess.display_height / 4, maxY - mess.display_height / 4);
        this.spill = this.add.image(point.x, point.y, "assets", "spill");
        this.spill.setTint(spillColors[Utilities.getRandomArrayIndex(spillColors.length)]);
        this.spill.setDisplaySize(mess.display_width, mess.display_height);
        this.spill.setInteractive();
        this.spill.setDepth(5);
        this.spill.setDataEnabled();
        this.spill.setData("cleaned", false);
        this.spill.setName(mess.name);
    }

    /**
     * Creates desk items (including garbage) which are spread across the desk randomly
     */
    createDeskItems(): void {
        var minX = this.deskTop.getBottomLeft().x - 10;
        var minY = this.deskTop.getTopLeft().y - 10;
        var maxX = this.deskTop.getBottomRight().x - 10;
        var maxY = this.deskTop.getBottomLeft().y - 10;

        DeskItem.ALL_ITEMS.forEach((item: DeskItem) => {
            for (var i = 0; i < item.quantity; i++) {
                var point = Utilities.getRandomPoint(minX + item.display_width / 4, maxX - item.display_width / 4, minY + item.display_height / 4, maxY - item.display_height / 4);
                let deskItem: DropZoneSprite = new DropZoneSprite(this, item.name, point.x, point.y, "assets", item.name);
                deskItem.setDisplaySize(item.display_width, item.display_height);
                deskItem.setInteractive();
                deskItem.setName(item.name);
                deskItem.type = "deskItem";
                deskItem.setAngle(Math.random() * (100) + 5);
                deskItem.setDepth(10);
                deskItem.setData('id', this.itemID++);
                deskItem.setData('startAngle', deskItem.angle);
                this.input.setDraggable(deskItem);
                if (item.id === 5 || item.id === 6) {
                    this.garbageItems.push(deskItem);
                }
                this.allDeskItems.push(deskItem);
            }
        });
    }

    /**
     * Creates an item on the cart that can be put on the desk (school supplies)
     */
    createCartItem(ob: TiledDropZone): void {
        var item = DeskItem.SUPPLIES.find((i: DeskItem) => {
            return i.name === ob.name;
        });

        for (var i = 0; i < 5; i++) {
            let cartItem: DropZoneSprite = new DropZoneSprite(this, item.name, ob.x + ob.width / 2, ob.y + ob.height / 2, "assets", item.name);
            item.startX = cartItem.x;
            item.startY = cartItem.y;
            cartItem.setDisplaySize(item.display_width, item.display_height);
            cartItem.setInteractive();
            cartItem.setDepth(10);
            cartItem.setAngle(item.angle);
            cartItem.setName(item.name);
            cartItem.type = "cartItem";
            cartItem.setDataEnabled();
            cartItem.setData('id', this.itemID++);
            cartItem.setData('startAngle', cartItem.angle);
            this.input.setDraggable(cartItem);
            this.allDeskItems.push(cartItem);
        }
    }

    /**
     * Creates a cleaning item (sponge or rag) that can be used to clean the spill
     */
    createCleaningItem(ob: TiledDropZone): void {
        var item = DeskItem.CLEANING_ITEM;
        this.sponge = new DropZoneSprite(this, item.name, ob.x + ob.width / 2, ob.y + ob.height / 2, "assets", item.name);
        item.startX = this.sponge.x;
        item.startY = this.sponge.y;
        this.sponge.setDisplaySize(item.display_width, item.display_height);
        this.sponge.setInteractive();
        this.sponge.setDepth(10);
        this.sponge.setAngle(ob.rotation);
        this.sponge.setTint(0xe5d824);
        this.sponge.setName(item.name);
        this.sponge.type = "cleaningItem";
        this.sponge.setData('id', this.itemID++);
        this.sponge.setData('startAngle', this.sponge.angle);
        this.input.setDraggable(this.sponge);
        this.allDeskItems.push(this.sponge);
    }

    /**
     * Checks to see if the desk is organized/cleaned:
     * --All necessary desk supplies are on desk
     * --All garbage has been put in the trash
     * --No unnecessary items on the desk top
     * --If there is a spill, it's cleaned
     */
    isDeskOrganized(): boolean {
        // First, check that each dropzone has one item dropped in it and the trash has all garbage in it
        for (var dz of this.dropZones) {
            if (dz.type === "DropZone" && dz.name !== "cart") {
                // A drop zone doesn't have an item dropped in it
                if (dz.children.length !== 1) {
                    return false;
                }
            }
            else if (dz.type === "TrashDropZone") {
                var numberOfItemsDropped = dz.children.length;
                // The trash drop zone doesn't contain all garbage items
                if (numberOfItemsDropped < Utilities.getNumberOfItems(GameState.current.deskGarbageItems)) {
                    return false;
                }
            }
        }
        // If that passes, check now if there are unnecessary items on the desk
        if (Utilities.checkIfExtraItemsOnDesk(this.deskTop, this.allDeskItems)) {
            return false;
        }
        // Check if the spill is cleaned
        if (GameState.current.spill && !this.spill.getData('cleaned')) {
            return false;
        }
        // If all of that passes, the desk is organized
        return true;
    }

    /**
     * Checks if the desk is cleaned/organized properly.
     * Will either allow the player to move on or tell the player to keep organizing.
     */
    checkDeskButtonHandler(button: PhaserButton): void {
        var modalConfig;
        var modalOptions;
        var modal;
        modalConfig = Utilities.getModalConfig();
        if (this.isDeskOrganized()) {
            modalConfig.title = TranslateApi.translate(this.translateString + "deskDone");

            if (GameState.current.deskNumber === GameState.NUMBER_OF_DESKS) {
                // This was the last desk in the classroom, game is done
                modalOptions = [{ displayText: TranslateApi.translate("student.postgame.button"), id: 2 }];
            }
            else {
                modalOptions = [{ displayText: TranslateApi.translate(this.translateString + "nextDesk"), id: 1 }];
            }
            modal = new PhaserModal(this, 0, 0, modalOptions, modalConfig);
            modal.setPosition(this.gameWidth / 2 - modal.width / 2, this.gameHeight / 2 - modal.height / 2);
            modal.setDepth(10);
            modal.on('optionClicked', (op: ModalOption) => {
                modal.destroy();
                if (op.id === 2) {
                    this.input.removeAllListeners();
                    this.input.setDefaultCursor("default");
                    this.scene.add("EndGameScene", EndGameScene, true);
                    this.scene.remove(this);
                }
                else {
                    // Initialize game state for the next DeskScene
                    GameState.current.deskNumber = GameState.current.deskNumber + 1;
                    GameState.current.deskGarbageItems = Utilities.initializeGarbageItems();
                    GameState.current.deskSupplies = Utilities.initializeDeskSupplies();
                    GameState.current.mapKey = "desk_" + GameState.current.deskOrder.pop();
                    GameState.current.spill = Math.random() >= 0.5; // Randomly choose if there is a spill or not
                    // Stop the current scene and start a new one with the new game state
                    this.scene.add("DeskScene", DeskScene, true);
                    this.scene.remove(this);
                }
            });
        }
        else {
            modalConfig.title = TranslateApi.translate(this.translateString + "notDone");
            modalConfig.titleSize = 20;
            modalOptions = [{ displayText: TranslateApi.translate("common.ok"), id: 1 }];
            modal = new PhaserModal(this, 0, 0, modalOptions, modalConfig);
            modal.setPosition(this.gameWidth / 2 - modal.width / 2, this.gameHeight / 2 - modal.height / 2);
            modal.setDepth(10);
            modal.on('optionClicked', () => {
                modal.destroy();
                button.setEnabled(true);
            });
        }
    }

    /**
     * Checks for stuff on the desk that student did incorrectly so we can give them a hint (draw their attention to) whatever they missed
     * First checks if any of the necessary items for the desk layout are empty (show the outlined dropzone)
     * Then checks if there is any visible garbage still on the desk (wiggle the garbage)
     * Then checks if there are any unneeded items on the desk (wiggle the item)
     * Finally, checks if the spill is still there (flash the spill, wiggle the sponge)
     */
    giveHint(): void {
        var dz: DropZone;
        //  Outline any empty dropzones
        for (dz of this.dropZones.filter(d => d.type === "DropZone")) {
            if (dz.children.length === 0) {
                dz.graphics.lineStyle(2, Color.COLORS.get("grey"));
                dz.graphics.strokeRectShape(dz.getBounds());
                // Only show outline of dropzone for one second
                setTimeout(() => {
                    dz.graphics.clear();
                }, 1000);
                return;
            }
        }

        // Draw attention to any visible garbage
        for (var garbage of this.garbageItems.filter(g => g.visible)) {
            this.drawAttentionTo(garbage);
            return;
        }

        // Draw attention to any stray items on the desk
        var deskTopBounds = this.deskTop.getBounds();
        var rect = new Phaser.Geom.Rectangle(this.deskTop.x, this.deskTop.y, deskTopBounds.width, deskTopBounds.height);
        for (var i of this.allDeskItems.filter(x => !x.getData("dropped"))) {
            if (rect.contains(i.x, i.y) && !i.getData('dropped')) {
                this.drawAttentionTo(i);
                return;
            }
        }

        // Finally, flash the spill and sponge
        if (this.spill != null && !this.spill.getData('cleaned')) {
            this.flashObject(this.spill);
            this.drawAttentionTo(this.sponge);
        }

    }

    // Simple tween that slightly wiggles object
    drawAttentionTo(gameObject: DropZoneSprite | Phaser.GameObjects.Image) {
        if (!this.tweens.isTweening(gameObject)) {
            this.tweens.add({
                targets: gameObject,
                onStart: () => {
                    this.children.bringToTop(gameObject);
                },
                angle: {
                    value: "+=20",
                    duration: 300
                },
                yoyo: true,
                repeat: 2,
            });
        }
    }

    flashObject(target: DropZoneSprite | Phaser.GameObjects.Image): void {
        if (!this.tweens.isTweening(target)) {
            this.tweens.add({
                targets: target,
                onStart: () => {
                    this.children.bringToTop(target);
                    target.alpha = 1;
                },
                alpha: {
                    value: 0.3,
                    duration: 500,
                },
                yoyo: true,
                repeat: 2,
            });
        }
    }

    update(): void {
        // Continuously check if the player is trying to clean up the spill
        if (this.spill) {
            if (!this.spill.getData("cleaned") && Utilities.isPlayerCleaningSpill(this.sponge, this.spill)) {
                // Make spill disappear
                this.tweens.add({
                    targets: this.spill,
                    alpha: { value: 0, duration: 800, ease: "Power1" },
                    onComplete: () => {
                        this.spill.setData("cleaned", true);
                    }
                });
            }
        }
    }

    isGarbage(gameObject: DropZoneSprite) {
        var item = DeskItem.ALL_ITEMS.find(x => x.name === gameObject.name);
        return item.id === 5 || item.id === 6;
    }
}



// WEBPACK FOOTER //
// ./src/games/OrganizeDeskGame/scenes/DeskScene.ts